import { coverResultExplanation } from './doctor-cover-copy';
import { formatCoverageDate } from '@/dashboard/coc/doctor-coverage-view';
import { uncoveredCoverIntervals } from './doctor-cover-gaps';
import { DaySlotPicker, useDaySlots } from '@/dashboard/appointments/DaySlotPicker';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { useEffect, useRef, useState } from 'react';
import { portalApiGet, portalApiPost, PortalApiError } from '@/lib/portal-api';
import ModalShell from '@/dashboard/ui/ModalShell';
import AcButton from '@/dashboard/ui/AcButton';
import { AcSelect, FormField } from '@/dashboard/ui/FormControl';
import { formatIstTime } from '@/lib/datetime-ist';
import { appointmentStatusDisplayLabel } from '@/dashboard/ui/status-canon';
import './doctor-cover-results.css';
export interface CoverResultRow {
  appointmentId: number;
  patientName: string | null;
  doctorName: string | null;
  eligible: boolean;
  outcome: string;
  reviewReason?: string | null;
  followup: boolean;
  expected: {
    doctorId: number | null;
    appointmentTime: string;
    status: string;
    updatedAt: string | null;
  };
  coverExpected: Record<string, unknown>;
  decisionId: string | null;
  moveOperationId: string | null;
  canUndo: boolean;
  contactOwner: 'receptionist' | 'coc' | null;
  contact: null | { operationId: string; outcome: string; note: string; method: string };
  rebookedFrom?: number;
  rebooking?: null | { appointmentId: number; reason: string };
}
export interface CoverResults {
  report: {
    report_id: string;
    doctor_id: number;
    affected_date: string;
    starts_at?: string | null;
    ends_at?: string | null;
    detail?: string;
  };
  bookings: CoverResultRow[];
  coordination: {
    id: number;
    legacy_doctor_id: number;
    status: string;
    starts_at: string;
    ends_at: string;
  }[];
  counts: { affected: number; withTreatingTeam: number };
  decision: null | {
    operationId: string;
    state: string;
    absenceHours?: { startsAt: string; endsAt: string } | null;
    note?: string | null;
  };
}

/** Application composition using existing modal, form controls and protected writers. */
export default function DoctorCoverResults({
  clinicId,
  clinicName,
  reportId,
  role,
  doctors,
  onClose,
  onChanged,
  initialAppointmentId,
}: {
  clinicId: number;
  clinicName: string;
  reportId: string;
  role: 'receptionist' | 'coc';
  doctors: readonly { id: number; name: string }[];
  onClose: () => void;
  onChanged: () => void;
  initialAppointmentId?: number;
}) {
  const [data, setData] = useState<CoverResults | null>(null),
    [selected, setSelected] = useState<number | null>(null),
    [panel, setPanel] = useState<'results' | 'contact' | 'review' | 'rebook'>('results');
  const [newTime, setNewTime] = useState(''),
    [newDate, setNewDate] = useState(''),
    [slotRetry, setSlotRetry] = useState(0);
  const slots = useDaySlots(null, panel === 'rebook' ? newDate : '', clinicId, slotRetry);
  const available =
    slots.kind === 'ready' &&
    slots.data.slots.some((slot) => slot.startIso === newTime && !slot.taken && !slot.past);
  const [outcome, setOutcome] = useState(''),
    [note, setNote] = useState(''),
    [doctor, setDoctor] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const epoch = useRef(0),
    saving = useRef(false),
    pending = useRef<{ path: string; body: Record<string, unknown> } | null>(null);
  const storageKey = useRef<string | null>(null),
    [ready, setReady] = useState(false);
  const row = data?.bookings.find((r) => r.appointmentId === selected);
  const contactSaved =
    !!outcome && row?.contact?.outcome === outcome && (row.contact.note ?? '') === note;
  async function load() {
    const token = epoch.current;
    try {
      const next = await portalApiGet<CoverResults>(
        `/reception/doctor-absence-reports/${reportId}/affected-bookings?clinicId=${clinicId}`,
      );
      if (token === epoch.current) setData(next);
    } catch (e) {
      if (token === epoch.current) {
        setData(null);
        if (e instanceof PortalApiError && [401, 403].includes(e.status)) {
          setReady(false);
          pending.current = null;
          if (storageKey.current) sessionStorage.removeItem(storageKey.current);
          storageKey.current = null;
        }
      }
      throw e;
    }
  }

  useEffect(() => {
    const token = ++epoch.current;
    let active = true;
    setReady(false);
    storageKey.current = null;
    setData(null);
    setSelected(null);
    setPanel('results');
    setError('');
    pending.current = null;
    const client = getSupabaseBrowserClient();
    let actor: string | null = null;
    void (async () => {
      actor = client ? ((await client.auth.getSession()).data.session?.user.id ?? null) : null;
      if (!actor) throw new Error('Session unavailable');
      const key = `doctor-cover-results:v1:${actor}:${role}:${clinicId}:${reportId}`;
      const raw = sessionStorage.getItem(key);
      const saved = raw ? JSON.parse(raw) : null;
      if (!active || token !== epoch.current) return;
      if (
        saved?.pending &&
        (saved.pending.body?.clinicId !== clinicId ||
          saved.pending.body?.reportId !== reportId ||
          ![
            '/reception/doctor-cover/contact',
            '/reception/doctor-cover/review',
            '/reception/doctor-cover/undo',
            '/reception/doctor-cover/rebook-arrived',
            '/reception/doctor-cover/rebook-pending',
          ].includes(saved.pending.path))
      )
        throw new Error('Draft scope changed');
      storageKey.current = key;
      if (saved) {
        setSelected(saved.selected ?? null);
        setPanel(
          ['results', 'contact', 'review', 'rebook'].includes(saved.panel)
            ? saved.panel
            : 'results',
        );
        setOutcome(saved.outcome ?? '');
        setNote(saved.note ?? '');
        setDoctor(saved.doctor ?? '');
        setNewTime(saved.newTime ?? '');
        setNewDate(saved.newDate ?? '');
        pending.current = saved.pending ?? null;
      }
      setReady(true);
      await load();
    })().catch(() => {
      if (active && token === epoch.current)
        setError('Cover results could not be restored. Retry after checking the session.');
    });
    const subscription = client?.auth.onAuthStateChange((_event, session) => {
      if (actor && session?.user.id !== actor) {
        epoch.current++;
        setData(null);
        setReady(false);
        pending.current = null;
        if (storageKey.current) sessionStorage.removeItem(storageKey.current);
        storageKey.current = null;
        setError('Session changed. Reopen cover results.');
      }
    });
    return () => {
      active = false;
      epoch.current++;
      subscription?.data.subscription.unsubscribe();
    };
  }, [clinicId, reportId, role]);
  const initialOpened = useRef(false);
  useEffect(() => {
    if (initialOpened.current || !ready || !data || initialAppointmentId === undefined) return;
    initialOpened.current = true;
    // An uncertain saved operation takes precedence over a new row-open intent.
    if (pending.current) return;
    const target = data.bookings.find((r) => r.appointmentId === initialAppointmentId);
    if (target?.contactOwner === role) select(target, 'contact');
    else if (
      role === 'coc' &&
      target?.eligible &&
      ['no_cover', 'outside_cover', 'capacity'].includes(target.outcome)
    )
      select(target, 'rebook');
    else setError('This booking no longer needs this action. Review its saved result.');
  }, [ready, data, initialAppointmentId, role]);
  function keepDraft() {
    if (!storageKey.current) throw new Error('Draft unavailable');
    sessionStorage.setItem(
      storageKey.current,
      JSON.stringify({
        selected,
        panel,
        outcome,
        note,
        doctor,
        newTime,
        newDate,
        pending: pending.current,
      }),
    );
  }
  useEffect(() => {
    if (!ready) return;
    try {
      keepDraft();
    } catch {
      setReady(false);
      setError('This browser could not keep the draft. Save is unavailable.');
    }
  }, [ready, selected, panel, outcome, note, doctor, newTime, newDate, busy]);

  async function send(path: string, body: Record<string, unknown>) {
    if (saving.current || !ready) return;
    saving.current = true;
    setBusy(true);
    setError('');
    const token = epoch.current;
    try {
      pending.current ??= {
        path,
        body: { clinicId, reportId, requestId: crypto.randomUUID(), ...body },
      };
      keepDraft();
      await portalApiPost(pending.current.path, pending.current.body);
      if (token !== epoch.current) return;
      await load();
      if (token !== epoch.current) return;
      pending.current = null;
      if (storageKey.current) sessionStorage.removeItem(storageKey.current);
      if (path !== '/reception/doctor-cover/contact' || panel !== 'contact') {
        setPanel('results');
        setSelected(null);
      }
      onChanged();
    } catch (e) {
      if (token !== epoch.current) return;
      const status = e instanceof PortalApiError ? e.status : 0;
      if ([400, 401, 403, 409].includes(status)) pending.current = null;
      if ([401, 403].includes(status)) {
        setData(null);
        setReady(false);
        if (storageKey.current) sessionStorage.removeItem(storageKey.current);
        storageKey.current = null;
      }
      setError(
        e instanceof PortalApiError && e.code === 'clinic_rebooking_reason_blocked_for_ravi'
          ? 'Blocked for Ravi: the cancellation list has no Doctor unavailable reason. The visit has not been closed.'
          : status === 409
            ? 'Booking changed. Reload before saving.'
            : status === 403
              ? 'Access is no longer available.'
              : 'Save not confirmed. Your entries are kept. Retry.',
      );
    } finally {
      saving.current = false;
      if (token === epoch.current) setBusy(false);
    }
  }
  function select(r: CoverResultRow, target: 'contact' | 'review' | 'rebook') {
    setSelected(r.appointmentId);
    setPanel(target);
    setOutcome('');
    setNote('');
    setDoctor('');
    setNewTime('');
    setNewDate('');
    setError('');
  }
  const locked = !ready || busy || pending.current !== null;
  const title =
    panel === 'results'
      ? 'Review cover results'
      : panel === 'contact'
        ? role === 'receptionist'
          ? 'Record in-person update'
          : 'Patient contact'
        : panel === 'rebook'
          ? 'Rebook appointment'
          : row?.followup
            ? 'Review follow-up'
            : 'Review doctor choice';
  const needReception =
    data?.bookings.filter((r) => ['manual_review', 'undone'].includes(r.outcome)).length ?? 0;
  const moved = data?.bookings.filter((r) => r.outcome === 'moved').length ?? 0;
  const withCoc =
    data?.bookings.filter((r) =>
      ['followup_review', 'outside_cover', 'capacity', 'no_cover'].includes(r.outcome),
    ).length ?? 0;
  const choices =
    data?.coordination.filter(
      (c) =>
        c.status === 'confirmed' &&
        row &&
        Date.parse(c.starts_at) <= Date.parse(row.expected.appointmentTime) &&
        Date.parse(c.ends_at) > Date.parse(row.expected.appointmentTime),
    ) ?? [];
  const gaps = data
    ? uncoveredCoverIntervals(
        data.decision?.absenceHours?.startsAt ?? data.report.starts_at,
        data.decision?.absenceHours?.endsAt ?? data.report.ends_at,
        data.coordination,
      )
    : null;
  return (
    <ModalShell
      title={title}
      eyebrow={`${clinicName} · ${role === 'coc' ? 'COC' : 'Reception'}`}
      size={panel === 'results' ? 'lg' : 'default'}
      trapFocus
      closeDisabled={busy || pending.current !== null}
      onClose={onClose}
      testId="doctor-cover-results"
      footer={
        <>
          <AcButton
            variant="secondary"
            disabled={busy || pending.current !== null}
            onClick={() =>
              panel === 'results' || (panel === 'contact' && role === 'coc')
                ? onClose()
                : setPanel('results')
            }
          >
            {panel === 'results' ? 'Done' : 'Back'}
          </AcButton>
          {pending.current && (
            <AcButton
              variant="primary"
              disabled={!ready || busy}
              onClick={() => void send(pending.current!.path, pending.current!.body)}
            >
              Retry saved request
            </AcButton>
          )}
          {!pending.current && panel === 'contact' && row && (
            <AcButton
              variant="primary"
              disabled={!ready || busy || !outcome || contactSaved}
              onClick={() =>
                void send('/reception/doctor-cover/contact', {
                  appointmentId: row.appointmentId,
                  expected: row.coverExpected,
                  outcome,
                  note,
                })
              }
            >
              {pending.current ? 'Retry save' : 'Save outcome'}
            </AcButton>
          )}
          {!pending.current && panel === 'review' && row && (
            <AcButton
              variant="primary"
              disabled={!ready || busy || !doctor || row.contact?.outcome !== 'agreed'}
              onClick={() =>
                void send('/reception/doctor-cover/review', {
                  appointmentId: row.appointmentId,
                  expected: row.coverExpected,
                  decisionId: row.decisionId,
                  contactId: row.contact?.operationId,
                  doctorId: Number(doctor),
                })
              }
            >
              {pending.current ? 'Retry save' : 'Save doctor change'}
            </AcButton>
          )}
          {!pending.current && panel === 'rebook' && row && (
            <AcButton
              variant="primary"
              disabled={
                !ready || busy || !doctor || !available || row.contact?.outcome !== 'agreed'
              }
              onClick={() =>
                void send(
                  row.expected.status === 'arrived' || row.expected.status === 'checked_in'
                    ? '/reception/doctor-cover/rebook-arrived'
                    : '/reception/doctor-cover/rebook-pending',
                  {
                    appointmentId: row.appointmentId,
                    expected: row.coverExpected,
                    contactId: row.contact?.operationId,
                    doctorId: Number(doctor),
                    appointmentTime: newTime,
                    ...(['booked', 'confirmed'].includes(row.expected.status)
                      ? { decisionId: row.decisionId }
                      : {}),
                  },
                )
              }
            >
              Save agreed appointment
            </AcButton>
          )}
        </>
      }
    >
      <div className="cover-results" data-cover-panel={panel}>
        {error && <p role="alert">{error}</p>}
        {!data ? (
          <>
            <p role="status">{error ? 'Results unavailable.' : 'Loading cover results…'}</p>
            <AcButton
              variant="secondary"
              onClick={() =>
                void load().catch(() => setError('Cover results could not be loaded. Retry.'))
              }
            >
              Retry
            </AcButton>
          </>
        ) : panel === 'results' ? (
          <>
            <div className="cover-results__summary">
              <strong>
                {data.decision?.state === 'no_cover'
                  ? 'No cover · COC rebooking'
                  : `${moved} moved · ${needReception} need you`}
              </strong>
              <span>
                {data.counts.affected} affected bookings · {withCoc} with COC
              </span>
              {data.counts.withTreatingTeam > 0 && (
                <span>
                  {data.counts.withTreatingTeam} appointment
                  {data.counts.withTreatingTeam === 1 ? '' : 's'} with treating team · kept
                </span>
              )}
            </div>
            {data.decision?.state === 'confirmed' && gaps !== null && gaps.length > 0 && (
              <p className="cover-results__gap">
                Uncovered:{' '}
                {gaps
                  .map(
                    ([a, b]) =>
                      `${formatIstTime(new Date(a).toISOString())}–${formatIstTime(new Date(b).toISOString())} IST`,
                  )
                  .join(', ')}{' '}
                · COC agrees a new time.
              </p>
            )}
            <table className="cover-results__table">
              <thead>
                <tr>
                  <th>Patient / time</th>
                  <th>Doctor / result</th>
                  <th>Contact</th>
                  <th>
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.bookings.map((r) => {
                  const told = ['informed', 'agreed'].includes(r.contact?.outcome ?? '');
                  return (
                    <tr key={r.appointmentId}>
                      <td>
                        <strong>{r.patientName ?? 'Patient name unavailable'}</strong>
                        <small>
                          {r.rebookedFrom && (
                            <>
                              {new Date(r.expected.appointmentTime).toLocaleDateString('en-IN', {
                                timeZone: 'Asia/Kolkata',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}{' '}
                              ·{' '}
                            </>
                          )}
                          {formatIstTime(r.expected.appointmentTime)} ·{' '}
                          {r.outcome === 'started'
                            ? 'In consultation'
                            : appointmentStatusDisplayLabel(r.expected.status)}
                        </small>
                      </td>
                      <td>
                        <strong>{r.doctorName ?? 'Doctor name unavailable'}</strong>
                        <small>{coverResultExplanation(r)}</small>
                        {r.rebooking && <small>New appointment #{r.rebooking.appointmentId}</small>}
                        {r.rebookedFrom && <small>Rebooked from #{r.rebookedFrom}</small>}
                      </td>
                      <td>
                        <span className={told ? 'cover-results__contact-confirmed' : undefined}>
                          {!r.contactOwner
                            ? 'No cover update needed'
                            : told
                              ? r.contact?.outcome === 'agreed'
                                ? 'Agreed'
                                : 'Told'
                              : r.contactOwner === 'receptionist'
                                ? 'Tell in person'
                                : 'COC to contact'}
                        </span>
                        {r.contactOwner && (
                          <small>
                            {r.contactOwner === 'receptionist'
                              ? 'Reception · at clinic'
                              : r.contact?.outcome === 'unreachable'
                                ? 'Could not reach · retry'
                                : 'Phone'}
                          </small>
                        )}
                        {r.contactOwner === role && !told && (
                          <AcButton
                            variant="secondary"
                            disabled={locked}
                            onClick={() =>
                              r.outcome === 'moved' && role === 'receptionist'
                                ? void send('/reception/doctor-cover/contact', {
                                    appointmentId: r.appointmentId,
                                    expected: r.coverExpected,
                                    outcome: 'informed',
                                    note: '',
                                  })
                                : select(r, 'contact')
                            }
                          >
                            {r.outcome === 'moved' && role === 'receptionist'
                              ? 'Mark told'
                              : 'Record update'}
                          </AcButton>
                        )}
                      </td>
                      <td>
                        {role === 'coc' &&
                        r.eligible &&
                        ['no_cover', 'outside_cover', 'capacity'].includes(r.outcome) ? (
                          <AcButton
                            disabled={locked}
                            onClick={() =>
                              select(
                                r,
                                r.contact?.outcome === 'agreed'
                                  ? 'rebook'
                                  : r.contactOwner === 'coc'
                                    ? 'contact'
                                    : 'rebook',
                              )
                            }
                          >
                            Rebook
                          </AcButton>
                        ) : r.canUndo && role === 'receptionist' ? (
                          <AcButton
                            variant="secondary"
                            disabled={locked}
                            onClick={() =>
                              void send('/reception/doctor-cover/undo', {
                                appointmentId: r.appointmentId,
                                decisionId: r.moveOperationId,
                              })
                            }
                          >
                            Undo
                          </AcButton>
                        ) : r.eligible &&
                          ((role === 'receptionist' &&
                            ['manual_review', 'undone'].includes(r.outcome)) ||
                            (role === 'coc' && r.outcome === 'followup_review')) ? (
                          <AcButton
                            variant="secondary"
                            disabled={locked}
                            onClick={() => select(r, 'review')}
                          >
                            Review
                          </AcButton>
                        ) : (
                          <span className="cover-results__owner-status">
                            {r.outcome === 'started'
                              ? 'Kept'
                              : ['reviewed', 'rebooked', 'clinic_rebooked'].includes(r.outcome)
                                ? 'Updated'
                                : r.eligible
                                  ? 'With COC'
                                  : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : row ? (
          <>
            {panel === 'contact' && contactSaved && !pending.current && (
              <p className="cover-results__saved" role="status">
                Conversation saved.
              </p>
            )}
            {['review', 'rebook'].includes(panel) ? (
              <>
                <h3 className="cover-results__patient-title">
                  {row.patientName ?? 'Patient name unavailable'}
                </h3>
                <p className="cover-results__muted">
                  {panel === 'rebook' ? 'Current: ' : ''}
                  {formatCoverageDate(
                    new Intl.DateTimeFormat('en-CA', {
                      timeZone: 'Asia/Kolkata',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }).format(new Date(row.expected.appointmentTime)),
                  )}{' '}
                  · {formatIstTime(row.expected.appointmentTime)}
                  {panel === 'rebook'
                    ? ` · ${row.doctorName ?? 'Doctor name unavailable'}`
                    : ' IST'}
                </p>
                {panel === 'review' && (
                  <p className="cover-results__review-warning">{coverResultExplanation(row)}</p>
                )}
              </>
            ) : (
              <>
                <div className="cover-results__context">
                  <div>
                    <strong>{row.patientName ?? 'Patient name unavailable'}</strong>
                    <small>
                      {formatCoverageDate(
                        new Intl.DateTimeFormat('en-CA', {
                          timeZone: 'Asia/Kolkata',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                        }).format(new Date(row.expected.appointmentTime)),
                      )}{' '}
                      · {formatIstTime(row.expected.appointmentTime)} IST ·{' '}
                      {row.doctorName ?? 'Doctor name unavailable'}
                    </small>
                  </div>
                  {panel === 'contact' && (
                    <span className="cover-results__contact-kind">
                      {role === 'receptionist' ? 'At clinic' : 'Phone'}
                    </span>
                  )}
                </div>
                <p>{coverResultExplanation(row)}</p>
              </>
            )}

            {panel === 'rebook' ? (
              <>
                {row.contactOwner === 'receptionist' && (
                  <p className="cover-results__review-warning">
                    At the clinic · Reception records the patient’s agreement in person.
                  </p>
                )}
                {row.contact?.outcome !== 'agreed' && (
                  <p role="status" className="cover-results__review-warning">
                    Patient agreement is needed before saving.
                  </p>
                )}
                <div className="cover-results__agreed-appointment">
                  <strong>Agreed appointment</strong>
                  <fieldset disabled={locked}>
                    <DaySlotPicker
                      compact
                      presentation="select"
                      date={newDate}
                      onDateChange={(value) => {
                        setNewDate(value);
                        setNewTime('');
                      }}
                      slots={slots}
                      selectedIso={newTime}
                      onSelectIso={setNewTime}
                      disabled={locked}
                      idPrefix="cover-rebook"
                    />
                    {slots.kind === 'error' && (
                      <AcButton disabled={locked} onClick={() => setSlotRetry((n) => n + 1)}>
                        Retry available times
                      </AcButton>
                    )}
                    <FormField id="cover-rebook-doctor" label="Agreed doctor">
                      <AcSelect
                        id="cover-rebook-doctor"
                        value={doctor}
                        onChange={(e) => setDoctor(e.target.value)}
                      >
                        <option value="">Choose doctor</option>
                        {doctors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </AcSelect>
                    </FormField>
                  </fieldset>
                </div>
                {row.contact?.note && <p>Patient preference: {row.contact.note}</p>}
                <p className="cover-results__muted">
                  Save only after the patient agrees. COC keeps the original history.
                </p>
              </>
            ) : panel === 'contact' ? (
              <>
                <fieldset disabled={locked}>
                  <FormField
                    id="cover-contact-outcome"
                    label={role === 'receptionist' ? 'In-person outcome' : 'Call outcome'}
                  >
                    <AcSelect
                      id="cover-contact-outcome"
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                    >
                      <option value="">Choose outcome</option>
                      <option value="informed">Patient informed</option>
                      <option value="agreed">Patient agreed</option>
                      {role === 'coc' && <option value="unreachable">Could not reach</option>}
                      <option value="different_time">Different time requested</option>
                    </AcSelect>
                  </FormField>
                  <FormField id="cover-contact-note" label="Note (optional)">
                    <textarea
                      className="ac-input"
                      rows={2}
                      id="cover-contact-note"
                      maxLength={500}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="What did the patient agree to?"
                    />
                  </FormField>
                </fieldset>
                {row.contact?.note && <p>Saved: {row.contact.note}</p>}
                <p className="cover-results__muted">
                  Records a conversation. No call or message is sent.
                </p>
                {role === 'coc' && row.eligible && row.outcome === 'followup_review' && (
                  <AcButton disabled={locked} onClick={() => setPanel('review')}>
                    Review follow-up
                  </AcButton>
                )}
                {role === 'coc' &&
                  row.eligible &&
                  ['no_cover', 'outside_cover', 'capacity'].includes(row.outcome) && (
                    <AcButton
                      disabled={locked || row.contact?.outcome !== 'agreed'}
                      onClick={() => setPanel('rebook')}
                    >
                      Agree a new appointment
                    </AcButton>
                  )}
                {role === 'receptionist' && (
                  <p>COC arranges any new appointment using this update.</p>
                )}
              </>
            ) : (
              <>
                <p>
                  {row.contactOwner === 'receptionist'
                    ? 'Speak to the patient here before saving.'
                    : row.followup
                      ? 'Check follow-up care and the patient’s preference.'
                      : 'Use the patient preference recorded by COC.'}
                </p>
                <span
                  className={
                    row.contact?.outcome === 'agreed'
                      ? 'cover-results__agreement cover-results__agreement--agreed'
                      : 'cover-results__agreement'
                  }
                >
                  {row.contact?.outcome === 'agreed'
                    ? 'Patient agreed'
                    : row.contact?.outcome === 'informed'
                      ? 'Patient informed'
                      : row.contact?.outcome === 'unreachable'
                        ? 'Could not reach'
                        : row.contact?.outcome === 'different_time'
                          ? 'Different time requested'
                          : 'Patient agreement needed'}
                </span>
                {row.contact?.note && <p>{row.contact.note}</p>}
                {row.contactOwner === role && row.contact?.outcome !== 'agreed' && (
                  <AcButton
                    variant="secondary"
                    disabled={locked}
                    onClick={() => setPanel('contact')}
                  >
                    {role === 'receptionist' ? 'Record in-person update' : 'Record patient contact'}
                  </AcButton>
                )}
                <FormField id="cover-review-doctor" label="Agreed doctor">
                  <AcSelect
                    id="cover-review-doctor"
                    disabled={locked}
                    value={doctor}
                    onChange={(e) => setDoctor(e.target.value)}
                  >
                    <option value="">Choose confirmed cover</option>
                    {choices.map((c) => (
                      <option key={c.id} value={c.legacy_doctor_id}>
                        {doctors.find((d) => d.id === c.legacy_doctor_id)?.name ??
                          'Doctor name unavailable'}
                      </option>
                    ))}
                  </AcSelect>
                </FormField>
                <p className="cover-results__muted">Time stays unchanged.</p>
              </>
            )}
          </>
        ) : (
          <p>The booking changed. Return to results.</p>
        )}
      </div>
    </ModalShell>
  );
}
